import React, {
	useContext,
	Fragment as Frag
} from 'react';
import PropTypes from 'prop-types';
import ReactPlaceholder from 'react-placeholder';

import {
	Divider,
	Grid,
	makeStyles
} from '@material-ui/core';

import Constants from "../utils/Constants";
import AvatarRowWithCol from './AvatarRowWithCol';
import AuthorLink from './AuthorLink';
import AuthorInfo from './AuthorInfo';
import ExtraHeaderInfo from './ExtraHeaderInfo';
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import MultipleAuthors from "./MultipleAuthors";
import ViewOriginalButton from "./ViewOriginalButton";
import Timestamp from "./Timestamp";
import ViewPatternButton from "./ViewPatternButton";
import SearchDetails from './SearchDetails';

const styles = makeStyles(theme => ({
	alignBtn: {
		"& > *:first-child": {
			marginLeft: theme.spacing(-1.25),
			padding: theme.spacing(1.25)
		},
	},

	authorLinkRow: {
		"& > *:first-child": {
			paddingTop: theme.spacing(.5),
			paddingBottom: theme.spacing(.5),
		},
	},

	avatarContentClassname: {
		marginTop: theme.spacing(-1),
	},

	sourceIcon: {
		opacity: "0.7",
		marginLeft: theme.spacing(1),
		color: theme.palette.text.secondary
	},

	sourceIconItem: {
		paddingLeft: theme.spacing(1)
	},

	extraHeaderInfo: {
		marginTop: theme.spacing(1.5)
	},

	viewPatternButton: {
		marginBottom: theme.spacing(-1)
	},

	viewOrigButton: {
		marginLeft: theme.spacing(1),
		marginBottom: theme.spacing(-1)
	},

	noWrap: {
		whiteSpace: "nowrap"
	}
}));

function ActivityInfo(props) {
	const authenticatedUser = useContext(AuthorizedUserContext);

	let classes = styles(),
		{fidoError, authorName, authorImage, authorLink, authorDesc, authorLoc, authorFollowers, authorFollowing, authorIp,
			hideAuthorInfo, sourceName, sourceHandle, sourceId, subSourceId, showTimestamp, time, showAvatarRow, expandHeaderInfo,
			twoLineSource, extraHeaders, twitterRedacted, authors, className, postLink, snapshot, revSnapshot,
			pattern, history, excludeHeaderInfoSent, viewPatternConfig, searchDetails} = props,
		ready = authorName != null || props.ready === true,
		anonymize = extraHeaders && authenticatedUser.ghosting,
		showSocialAuthorInfo = authorDesc || authorLoc || authorFollowers || authorFollowing,
		isGSuite = Constants.SOURCES.GSUITE_IDS.includes(sourceId) || Constants.SOURCES.GSUITE_IDS.includes(subSourceId),
		isGDoc = sourceId === Constants.SOURCES.IDS.GOOGLE_DOCS && subSourceId !== Constants.SOURCES.IDS.GSUIT_COMMENT,
		isGSuitComment = subSourceId === Constants.SOURCES.IDS.GSUIT_COMMENT,
		isSearch = sourceId === Constants.SOURCES.IDS.WEB_SEARCH,
		isTip = sourceName === 'tips',
		notRedacted = !twitterRedacted || twitterRedacted === Constants.ACTIVITY.TWITTER_REDACTION.SCRUB_GEO,
		infoSize = notRedacted && postLink ? 10 : 12;

	return (
		<div className={className}>
			{showAvatarRow ?
				<AvatarRowWithCol
					useIconFallback
					ready={ready}
					fidoError={fidoError}
					authorName={authorName}
					authorImage={authorImage}
					contentClassName={classes.avatarContentClassname}>
					<Grid
						container
						wrap="nowrap"
						justify="flex-start"
						alignItems="flex-end">
						<Grid item sm={10} md={10} lg={9} xl={9}>
							<ReactPlaceholder type='textRow' ready={ready} showLoadingAnimation={!fidoError} style={{width: "85%"}}>
								<div className={`${classes.alignBtn} ${twoLineSource ? "" : classes.authorLinkRow}`}>
									{isGDoc ? (
										 authenticatedUser.ghosting ? <b>Anonymized</b> :
										 <MultipleAuthors authors={authors} />
									) : (
										<AuthorLink
											name={authorName}
											ip={authorIp}
											sourceName={sourceName}
											sourceHandle={extraHeaders && !anonymize ? extraHeaders.from : sourceHandle}
											twoLines={twoLineSource}
											link={extraHeaders || isGSuite ? null : authorLink} />
									)}
									{showTimestamp ? (
										<Timestamp time={time} relative={false} />
									) : null}
								</div>
							</ReactPlaceholder>
						</Grid>
					</Grid>
				</AvatarRowWithCol> : null
			}
			<Grid
				container
				wrap="nowrap"
				alignItems="flex-end"
				justify="space-between">
				<Grid item xs={infoSize} sm={infoSize} md={infoSize} lg={infoSize} xl={infoSize}>
					{!hideAuthorInfo && 
						<>
							{showSocialAuthorInfo ?
								<AuthorInfo
									ready={ready}
									fidoError={fidoError}
									description={authorDesc}
									location={authorLoc}
									following={authorFollowing}
									followers={authorFollowers}
									twitterRedacted={twitterRedacted} /> : null
							}

							{!showSocialAuthorInfo && extraHeaders && !anonymize ?
								<ExtraHeaderInfo
										expanded={expandHeaderInfo}
										excludeSent={excludeHeaderInfoSent}
										headers={extraHeaders}
										className={classes.extraHeaderInfo} /> : null
										
							}

							{/* {!showSocialAuthorInfo && !extraHeaders && !anonymize && searchDetails ?
								<SearchDetails
									ready={ready}
									fidoError={fidoError}
									searchDetails={searchDetails}	 /> : null
							} */}
						</>
				}
				</Grid>
				<Grid item className={classes.noWrap}>
					{notRedacted && ready && !isGSuitComment ? (
						<Frag>
							<ViewPatternButton
								pattern={pattern}
								history={history}
								{...viewPatternConfig}
								className={classes.viewPatternButton} />
							{ sourceName !== 'googlePresentation' ? (
							<ViewOriginalButton
								isTip={isTip}
								isGDoc={isGDoc}
								isSearch={isSearch}
								postLink={postLink || revSnapshot}
								extraHeaders={extraHeaders}
								icon={isGDoc ? ["fal", "file-pdf"] : null}
								label={isGDoc ? "Doc Snapshot" : null}
								iconTitle={isGDoc ? "view doc snapshot" : null}
								className={classes.viewOrigButton} />
							) : null }
						</Frag>
					) : null}
				</Grid>
				<Grid item className={classes.noWrap}>
					{snapshot && notRedacted && ready && isGDoc && !isGSuitComment ? (
						<Frag>
							<ViewPatternButton
								pattern={pattern}
								history={history}
								{...viewPatternConfig}
								className={classes.viewPatternButton} />
							<ViewOriginalButton
								isGDoc
								isSearch={isSearch}
								offset="none"
								postLink={snapshot}
								icon={["fal", "file-pdf"]}
								className={classes.viewOrigButton} />
						</Frag>
					) : null}
				</Grid>
			</Grid>
		</div>
	);
}

ActivityInfo.defaultProps = {
	showSocialAuthorInfo: true,
	showAvatarRow: true
};

ActivityInfo.propTypes = {
	ready: PropTypes.bool,
	authors: PropTypes.string,
	authorName: PropTypes.string,
	authorImage: PropTypes.string,
	authorLink: PropTypes.string,
	authorDesc: PropTypes.string,
	authorLoc: PropTypes.string,
	authorFollowers: PropTypes.number,
	authorFollowing: PropTypes.number,
	time: PropTypes.string,
	showTimestamp: PropTypes.bool,
	sourceName: PropTypes.string,
	sourceHandle: PropTypes.string,
	sourceIcon: PropTypes.object,
	sourceId: PropTypes.number,
	subSourceId: PropTypes.string,
	twoLineSource: PropTypes.bool,
	fidoError: PropTypes.bool,
	extraHeaders: PropTypes.object,
	tip: PropTypes.object,
	hideAuthorInfo: PropTypes.bool,
	twitterRedacted: PropTypes.string,
	className: PropTypes.string,
	postLink: PropTypes.string,
	snapshot: PropTypes.string,
	revSnapshot: PropTypes.string,
	pattern: PropTypes.object,
	history: PropTypes.object,
	showAvatarRow: PropTypes.bool,
	expandHeaderInfo: PropTypes.bool,
	excludeHeaderInfoSent: PropTypes.bool,
	viewPatternConfig: PropTypes.object
};

export default ActivityInfo;
